import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_ENDPOINTS } from '../constants/api-endpoints';
import { STORAGE_KEYS } from '../constants/storage-keys';
import { environment } from '@env/environment';
import { AuthService } from './auth.service';
import { SemarAdditional, SemarStreamHandlers } from '../models/semar-chat.model';

/** Message shown before the auto-redirect when the session is no longer valid. */
const SESSION_EXPIRED_MESSAGE = 'Sesi Anda telah berakhir. Silakan masuk kembali.';

export interface SemarDeleteHistoryResponse {
  success: boolean;
  message: string;
}

/**
 * SEMAR "Ask AI" chat (slide-in panel) + backend (semarapi.maritimecloud.ai).
 *
 * Streaming pakai `fetch` (bukan HttpClient) karena butuh membaca body response
 * secara streaming (SSE). Karena `fetch` melewati authInterceptor, header
 * Authorization ditambahkan manual dari localStorage.
 */
@Injectable({ providedIn: 'root' })
export class SmartAiService {
  private http = inject(HttpClient);
  private auth = inject(AuthService);
  private readonly baseUrl = environment.apiSemarApiURL;

  private get accessToken(): string {
    return localStorage.getItem(STORAGE_KEYS.accessToken) ?? '';
  }

  /**
   * Reset konteks percakapan di backend.
   * @param interfaceType 'web' | 'semar_simulation' | 'document_analysis'
   */
  deleteHistory(interfaceType: string): Observable<SemarDeleteHistoryResponse> {
    return this.http.delete<SemarDeleteHistoryResponse>(
      `${this.baseUrl}${API_ENDPOINTS.semar.deleteHistory}`,
      { params: { interface_type: interfaceType } },
    );
  }

  /** Reset konteks Ask-AI (interface 'web') — fire & forget dengan log error. */
  resetHistory(): void {
    this.deleteHistory('web').subscribe({
      error: (err) => console.error('SEMAR: gagal reset riwayat chat (web):', err),
    });
  }

  /**
   * Streaming chat SEMAR.
   * SSE format: baris `data: {...}` — tiap JSON bisa berupa:
   *  - { chunk }                          → potongan teks
   *  - { finished:true, assistant, ... }  → pesan final
   *  - { additionalProp:true, type, data} → widget (map/vessel_photo/document/near_vessels)
   */
  streamChat(message: string, handlers: SemarStreamHandlers): void {
    const body = JSON.stringify({ interface_type: 'web', message });

    fetch(`${this.baseUrl}${API_ENDPOINTS.semar.chatStream}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.accessToken}`,
      },
      body,
    })
      .then(async (response) => {
        if (response.status === 401 || response.status === 403) {
          // fetch bypasses authInterceptor, so trigger the logout+redirect here.
          this.auth.handleSessionExpired();
          handlers.onError(SESSION_EXPIRED_MESSAGE);
          return;
        }
        if (!response.ok) {
          throw new Error(`HTTP ${response.status} ${response.statusText}`);
        }
        const reader = response.body?.getReader();
        const decoder = new TextDecoder('utf-8');
        if (!reader) throw new Error('Response body reader tidak tersedia');

        let buffer = '';
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });

            const lines = buffer.split('\n');
            for (const line of lines) {
              if (!line.startsWith('data:')) continue;
              const jsonStr = line.replace(/^data:\s*/, '').trim();
              if (!jsonStr) continue;
              try {
                const parsed = JSON.parse(jsonStr);
                if (parsed.finished === true) {
                  handlers.onDone(parsed.assistant ?? '');
                  return;
                }
                if (parsed.chunk) handlers.onChunk(parsed.chunk);
                if (parsed.additionalProp === true) {
                  handlers.onAdditional(parsed as SemarAdditional);
                }
              } catch (parseErr) {
                // Baris SSE belum lengkap terpotong antar-chunk — normal, tunggu chunk berikutnya.
                console.debug('SEMAR: melewati baris SSE yang belum lengkap:', jsonStr, parseErr);
              }
            }
            if (buffer.includes('\n')) buffer = '';
          }
        } catch (streamErr) {
          console.error('SEMAR: error membaca stream chat:', streamErr);
          handlers.onError('Maaf, terjadi kesalahan saat membaca data stream. Silakan coba lagi nanti.');
        } finally {
          reader.releaseLock();
        }
      })
      .catch((err: unknown) => {
        console.error('SEMAR: gagal terhubung ke server chat:', err);
        if (err instanceof TypeError) {
          handlers.onError('Maaf, tidak dapat terhubung ke server. Periksa koneksi internet Anda.');
        } else {
          handlers.onError('Maaf, terjadi kesalahan pada server. Silakan coba lagi nanti.');
        }
      });
  }
}
